import React, { useState, useMemo, useCallback } from 'react';
import { StarHalf } from '@phosphor-icons/react';
import classNames from 'classnames';
// @ts-ignore
import VideoThumbnail from 'react-video-thumbnail';

import { Popover } from '../../../components/general';
import { SideIcon } from '../../../components/icons';
import {
  getThumbnailSize,
  getVideoName,
} from '../../../components/video/getVideoContainerData';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';
import { useVideoBuilderStore } from '../../../stores/videoBuilder';

import { File, WildcardFiller, ClipSelection } from '../../../types';
import { round, getFileUrl, getThumbnail, msecToSec } from '../../../utils';

import { ClipPreviewController } from './clipPreview';
import SampleAddOverlay from './sampleAddOverlay';

interface Props {
  groupId?: string | null;
  video: File;
  onClick: () => void;
  onDelete?: () => void;
  isBig?: boolean;
  isSample?: boolean;
  isCurrent?: boolean;
  previewRef: React.RefObject<ClipPreviewController | null>;
}

const ThumbnailBlock: React.FC<Props> = (props) => {
  const {
    groupId,
    video,
    onClick,
    onDelete,
    isBig,
    isSample,
    isCurrent,
    previewRef,
  } = props;

  const { adminMode } = useUserStore();
  const { currentProject } = useProjectStore();
  const { clipSelections, deletingClipsIds } = useVideoBuilderStore();

  const [thumbnailImg, setThumbnailImg] = useState<string | null>(null);

  const previewData = useMemo(() => {
    let fileId = video.elementId;
    let fileName = video.name;
    let startTime = 0;
    let endTime: number | null = null;

    if (video.segment) {
      const parts = video.name.split('@');
      fileId = video.parentElementId ?? '';
      fileName = `${parts[0]}@${parts[1]}.mp4`;
      startTime = msecToSec(video.segment.startTimestampMilliseconds) + 0.033;
      endTime = msecToSec(video.segment.endTimestampMilliseconds) - 0.033;
    }

    const src = video.url ?? getFileUrl(fileId, fileName);

    return { src, startTime, endTime };
  }, [video]);

  const onMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      const targetElement = e.currentTarget as HTMLElement;
      previewRef.current?.show({ ...previewData, targetElement });
    },
    [previewData, previewRef],
  );

  const onMouseLeave = useCallback(() => {
    previewRef.current?.hide();
  }, [previewRef]);

  const isPortrait = currentProject?.videoMode === 'portrait';

  // find if a video is checked by default. returns number or false
  const isChecked = useMemo(() => {
    // find if the face already has selections data
    const foundSelections = clipSelections?.find(
      (group: ClipSelection) => group.faceId === groupId,
    );

    // if it does – find index of the wildcard it's used for
    if (foundSelections) {
      const i = foundSelections.wildcardsFiller.findIndex(
        (clip: WildcardFiller) => clip.clipId === video.elementId,
      );

      // return index + 1 for UI purposes and to avoid 0 to be considered as false
      if (i !== -1) return i + 1;
    }

    return false;
  }, [clipSelections, groupId, video.elementId]);

  const duration = video.segment
    ? msecToSec(video.segment.durationMilliseconds)
    : null;

  const { thumbWidth, thumbAspect } = getThumbnailSize(isPortrait, isBig);
  const videoName = getVideoName(video.name, !!video.segment);

  const thumbnailClass = classNames(
    'w-full rounded-md object-cover',
    thumbAspect,
  );

  return (
    <div className={classNames('block', thumbWidth)} key={video.elementId}>
      <button
        className={classNames(thumbnailClass, {
          'group relative transition-all duration-300': true,
          'shadow-salmon': isCurrent,
          'hover:scale-110': !isSample,
        })}
        onClick={onClick}
        onMouseEnter={!isSample || video.isSelected ? onMouseEnter : undefined}
        onMouseLeave={onMouseLeave}
      >
        {/* img overlay – contains delete & status icons, duration and checkbox */}
        <div
          className={classNames(
            thumbnailClass,
            'absolute flex flex-col items-center justify-center p-1 text-center',
          )}
        >
          {/* delete icon – shows X or spinner if deleting */}
          {!!onDelete && (
            <SideIcon
              type="delete"
              deleteType="clip"
              isProcessing={deletingClipsIds.includes(video.elementId)}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              hidePopover={isSample}
            />
          )}

          {video.isLowScore && (
            <SideIcon
              type="custom"
              icon={<StarHalf weight="fill" className="text-sm" />}
              variant="black"
              position="left"
              tooltipText="This clip may not feature the person clearly"
            />
          )}

          {/* below elements only present on clips */}

          {/* clip duration */}
          {duration && (
            <div className="absolute bottom-0 right-0 text-xs text-white z-[2]">
              <div className="min-w-[50px] pt-3 pr-1.5 pb-1.5 pl-4 rounded-tl-[100%] rounded-br-md bg-black-overlay-600">
                {round(duration, adminMode)}s
              </div>
            </div>
          )}

          {/* show wildcard number clip selected for (if any) */}
          {isChecked && (
            <div
              className={classNames(
                'absolute w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold',
                {
                  'bg-white text-success-900': !isCurrent,
                  'bg-primary-50 text-primary-900': isCurrent,
                },
              )}
            >
              {isChecked}
            </div>
          )}
        </div>

        {/* sample overlay */}
        {isSample && (
          <SampleAddOverlay
            element={video}
            addClass={classNames(thumbnailClass, 'text-[1.25rem]')}
          />
        )}

        {/* gets thumbnail from video and saves it in state */}
        {(!video.frameId || isPortrait) && !video.thumbnailUrl && (
          <VideoThumbnail
            videoUrl={getFileUrl(video.elementId, video.name)}
            thumbnailHandler={(thumbnail: string) => setThumbnailImg(thumbnail)}
            renderThumbnail={false}
          />
        )}

        {/* thumbnail image */}
        <img
          src={
            video.thumbnailUrl ?? thumbnailImg ?? getThumbnail(video.frameId)
          }
          className={thumbnailClass}
          alt={`${videoName} Thumbnail`}
        />
      </button>

      {/* file name */}
      <div className="flex flex-col mt-1 gap-1 text-xs leading-none break-words">
        <div>{videoName}</div>

        {adminMode && (
          <Popover
            placement="bottom"
            body={
              <div className="flex flex-col gap-1 text-xs leading-none">
                <div>
                  <span className="font-bold">ID</span>: {video.elementId}
                </div>
                {video.score && (
                  <div>
                    <span className="font-bold">Score</span>: {video.score}
                  </div>
                )}
                {video.segment && (
                  <div>
                    <span className="font-bold">Frames</span>:{' '}
                    {video.segment?.startFrame}-{video.segment?.endFrame}
                  </div>
                )}
              </div>
            }
          >
            <div className="w-min text-primary-900 underline whitespace-nowrap cursor-pointer">
              More Info
            </div>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default ThumbnailBlock;
